// SpendApprovalRequestForm (ticket 167) — the one-time form an owner/admin
// fills so a super-admin can approve the workspace for money-costing features.
import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { api, SPEND_PENDING_COPY, SPEND_DENIED_COPY } from './api';
import PhoneInput from './shared/PhoneInput';
import { phoneDigits } from './shared/phone';
import { US_STATES } from './shared/StateSelect';

const LICENCE_TYPES = [['nmls', 'NMLS'], ['real_estate', 'Real-estate licence']];
const EMPTY = { company_name: '', licence_type: 'nmls', licence_number: '', licence_state: '', phone: '' };

const nmlsDigits = (v) => v.replace(/\D/g, '').slice(0, 8);

// Mirrors the backend model: NMLS ID 3-8 digits, any other licence number 3-40
// chars (formats vary by state), 2-letter state, 10-digit phone.
export function validateSpendRequest(f) {
  const e = {};
  if (!f.company_name.trim()) e.company_name = 'Company name is required';
  if (!f.licence_type) e.licence_type = 'Choose a licence type';
  const num = f.licence_number.trim();
  if (f.licence_type === 'nmls') {
    if (!/^\d{3,8}$/.test(num)) e.licence_number = 'NMLS ID must be 3 to 8 digits';
  } else if (num.length < 3 || num.length > 40) {
    e.licence_number = 'Licence number must be 3 to 40 characters';
  }
  if (!/^[A-Za-z]{2}$/.test(f.licence_state)) e.licence_state = 'Choose a state';
  if (phoneDigits(f.phone).length !== 10) e.phone = 'Enter a 10-digit phone number';
  return e;
}

const FieldError = ({ id, msg }) => (msg
  ? <div className="text-[11px] text-red-700 mt-1" data-testid={`spend-form-error-${id}`}>{msg}</div>
  : null);

export default function SpendApprovalRequestForm({ open, onOpenChange, onRequested }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isNmls = form.licence_type === 'nmls';
  const setType = (v) => setForm((f) => ({
    ...f, licence_type: v, licence_number: v === 'nmls' ? nmlsDigits(f.licence_number) : f.licence_number,
  }));

  const submit = async (ev) => {
    ev?.preventDefault();
    if (submitting) return;
    const errs = validateSpendRequest(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setServerError('');
    try {
      const { data } = await api.post('/tenants/mine/spend-approval-request', {
        company_name: form.company_name.trim(),
        licence_type: form.licence_type,
        licence_number: form.licence_number.trim(),
        licence_state: form.licence_state.toUpperCase(),
        phone: phoneDigits(form.phone),
      });
      toast({ title: 'Request sent', description: SPEND_PENDING_COPY });
      onRequested?.(data);
      onOpenChange(false);
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (status === 409 && d?.code === 'spend_approval_denied') {
        setServerError(SPEND_DENIED_COPY);
        onRequested?.({ spend_approval: 'denied' });
      } else if (status === 409 && d?.code === 'already_approved') {
        onRequested?.({ spend_approval: 'approved' });
        onOpenChange(false);
      } else if (status === 429) {
        setServerError('Too many requests today, try again tomorrow');
      } else if (status === 422) {
        const first = Array.isArray(d) ? d[0]?.msg : d;
        setServerError(typeof first === 'string' && first ? first : 'Check the form and try again');
      } else {
        setServerError(typeof d === 'string' && d ? d : 'Could not send the request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="spend-approval-form">
        <DialogTitle className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>
          Request approval
        </DialogTitle>
        <DialogDescription className="text-[12px] text-slate-500">
          We confirm your licence before turning on AI, texting, email and e-signature.
        </DialogDescription>
        <form onSubmit={submit} noValidate className="space-y-3">
          <div>
            <Label htmlFor="spend-company" className="text-xs text-slate-500">Company name</Label>
            <Input id="spend-company" className="mt-1" value={form.company_name} autoComplete="organization"
              onChange={(e) => set('company_name')(e.target.value)} data-testid="spend-form-company" />
            <FieldError id="company_name" msg={errors.company_name} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Licence type</Label>
            <Select value={form.licence_type} onValueChange={setType}>
              <SelectTrigger className="mt-1" data-testid="spend-form-licence-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LICENCE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <FieldError id="licence_type" msg={errors.licence_type} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="spend-licence-number" className="text-xs text-slate-500">{isNmls ? 'NMLS ID' : 'Licence number'}</Label>
              <Input id="spend-licence-number" className="mt-1" value={form.licence_number}
                inputMode={isNmls ? 'numeric' : undefined}
                onChange={(e) => set('licence_number')(isNmls ? nmlsDigits(e.target.value) : e.target.value)}
                data-testid="spend-form-licence-number" />
              <FieldError id="licence_number" msg={errors.licence_number} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Licence state</Label>
              <Select value={form.licence_state} onValueChange={set('licence_state')}>
                <SelectTrigger className="mt-1" data-testid="spend-form-licence-state"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {US_STATES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <FieldError id="licence_state" msg={errors.licence_state} />
            </div>
          </div>
          <div>
            <Label htmlFor="spend-phone" className="text-xs text-slate-500">Phone</Label>
            <PhoneInput id="spend-phone" className="mt-1" value={form.phone} onChange={set('phone')} data-testid="spend-form-phone" />
            <FieldError id="phone" msg={errors.phone} />
          </div>
          {serverError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800" data-testid="spend-form-server-error">
              {serverError}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)}
              className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="spend-form-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting} data-testid="spend-form-submit"
              className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 shadow-md hover:brightness-105 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Send request
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
